import { display, remember, selectedAddresses, normalizeAddress, hasCurrentReview } from './session';

export default async function reviewReaders({ draft, snapshot, setup, url, active, show, waitFor, dispatch, services }) {
    const readerPassword = draft.readerPassword;
    const mqttPassword = draft.mqttPassword;
    if (!setup.mqttLoginReady && !mqttPassword)
        throw new Error('Enter the tenant MQTT password. It will be reused for this setup session.');
    const addresses = selectedAddresses(draft);
    if (!addresses.length) throw new Error('Choose discovered readers or enter an address.');
    if (addresses.length > 20) throw new Error('Select up to 20 readers per setup session.');
    draft.reviewErrors = {};
    draft.needsRegion = false;
    draft.advancedRegion = false;
    draft = {
        ...draft,
        credentialsRequested: false,
        readerPassword: '',
        mqttPassword: '',
        requestStatus: 'Checking the selected readers…'
    };
    await show(draft);
    if (!setup.mqttLoginReady || mqttPassword) {
        setup.mqttLoginReady = false;
        snapshot = await services.helperRequest(
            url,
            snapshot,
            'mqttLogin',
            { password: mqttPassword },
            active
        );
        if (!active()) return { draft, snapshot };
        setup.mqttLoginReady = true;
        remember(setup);
    }
    for (const [index, address] of addresses.entries()) {
        if (!active()) return { draft, snapshot };
        try {
            draft = display(
                {
                    ...setup.draft,
                    readerAddress: address,
                    readerName: '',
                    readerId: undefined,
                    checkingAddress: address
                },
                snapshot
            );
            await show({
                ...draft,
                requestStatus: `Checking reader ${index + 1} of ${addresses.length}: ${address}`
            });
            let reader = snapshot.readers.find(row => normalizeAddress(row.report?.address) === address);
            // A second review leaves completed readers and valid plans alone.
            if (reader?.setupStatus === 'Ready' || hasCurrentReview(reader)) continue;
            if (!reader || (readerPassword && reader.report?.status !== 'review')) {
                const input = {
                    address: draft.readerAddress,
                    username: draft.readerUsername || 'admin',
                    password: readerPassword
                };
                try {
                    snapshot = await services.helperRequest(url, snapshot, 'inspect', input, active);
                } catch (error) {
                    if (error.code !== 'READER_SESSION_CONFLICT' || !active()) throw error;
                    const conflictMessage = error.message;
                    const forceLogin = await new Promise(resolve => dispatch(
                        {
                            title: `Reader session in use: ${address}`,
                            message: conflictMessage,
                            okButtonText: 'Take over session',
                            cancelButtonText: 'Retry',
                            okAction: () => resolve(true),
                            cancelAction: () => resolve(false)
                        },
                        { verb: 'confirm', namespace: 'application', relation: 'user' }
                    ));
                    if (!active()) return { draft, snapshot };
                    // Retry only this reader once. Subsequent operations log in normally.
                    snapshot = await services.helperRequest(
                        url, snapshot, 'inspect', { ...input, forceLogin }, active
                    );
                }
                if (!active()) return { draft, snapshot };
                reader = snapshot.readers.find(row => normalizeAddress(row.report?.address) === address);
            }
            if (!reader) throw new Error('The reader inspection was not received. Try again.');
            draft = display({ ...draft, readerName: reader.name }, snapshot);
            const region = reader.report?.region;
            const configured =
                region?.regionName &&
                !/^(not set|choose region)$/i.test(region.regionName) &&
                region.standardName &&
                !/^(not set|choose standard)$/i.test(region.standardName);
            if (!configured && !reader.report?.selectedRegion) {
                if (!draft.regionName) {
                    draft.requestStatus =
                        'Select the country where this reader will operate, then choose Check readers again.';
                    draft.needsRegion = true;
                    draft.reviewErrors[address] = draft.requestStatus;
                    await show(draft);
                    continue;
                }
                snapshot = await services.helperRequest(
                    url,
                    snapshot,
                    'regionStandards',
                    { id: reader.id, regionName: draft.regionName },
                    active
                );
                if (!active()) return { draft, snapshot };
                const standards = snapshot.command.result;
                setup.standards = standards;
                setup.standardsReader = reader.id;
                setup.standardsRegion = draft.regionName;
                const standard =
                    standards.length === 1
                        ? standards[0]
                        : standards.find(row => row.standardName === draft.standardName);
                if (
                    !standard ||
                    standard.channelSelection ||
                    standard.lbtConfigurable ||
                    standard.hoppingConfigurable
                ) {
                    if (addresses.length > 1)
                        throw new Error(
                            'Select this reader on its own to choose its country radio settings.'
                        );
                    draft.advancedRegion = true;
                    draft.supportedStandards = standards.map(row => row.standardName).join('; ');
                    draft.standardName = standard?.standardName || '';
                    draft.requestStatus =
                        'This country requires radio settings. Choose the supported settings below.';
                    draft.reviewErrors[address] = draft.requestStatus;
                    await show(draft);
                    continue;
                }
                snapshot = await services.helperRequest(
                    url,
                    snapshot,
                    'selectRegion',
                    {
                        id: reader.id,
                        region: {
                            regionName: draft.regionName,
                            standardName: standard.standardName,
                            channels: [],
                            doLBT: null,
                            doHopping: null
                        }
                    },
                    active
                );
                if (!active()) return { draft, snapshot };
            }
            if (!hasCurrentReview(reader)) {
                await show({
                    ...draft,
                    requestStatus: `Preparing reader ${index + 1} of ${addresses.length}: ${address}`
                });
                await services.http.post(`${url}/prepare`, { readerId: reader.id });
                snapshot = await waitFor(url, next =>
                    ['review', 'needs-attention'].includes(
                        next.readers.find(row => row.id === reader.id)?.report?.status
                    )
                );
                if (!active()) return { draft, snapshot };
            }
            const prepared = snapshot.readers.find(row => row.id === reader.id);
            if (prepared?.report?.status === 'needs-attention')
                draft.reviewErrors[address] = prepared.report.error;
            draft = display(
                {
                    ...setup.draft,
                    requestStatus:
                        prepared?.report?.status === 'review'
                            ? 'Checking selected readers…'
                            : prepared?.report?.error ||
                              'The reader needs attention. Choose Check readers to retry.'
                },
                snapshot
            );
            await show(draft);
        } catch (error) {
            if (!active()) return { draft, snapshot };
            if ([401, 403, 404].includes(error.status)) throw error;
            draft = {
                ...setup.draft,
                reviewErrors: { ...setup.draft.reviewErrors, [address]: error.message }
            };
            await show(display(draft, snapshot));
        }
    }
    draft = display({ ...setup.draft, checkingAddress: '' }, snapshot);
    const ready = draft.readerProgress.filter(row =>
        selectedAddresses(draft).includes(row._id) && row.status === 'Awaiting confirmation'
    ).length;
    draft.requestStatus = ready
        ? `${ready} reader${ready === 1 ? ' is' : 's are'} ready to connect to ${snapshot.connection?.tenantTitle || 'this tenant'}. Choose Provision readers to continue.`
        : 'Check the messages on readers that need attention, then choose Retry.';
    return { draft, snapshot };
}
